import { Search, SlidersHorizontal, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Botao, CONTROLE_BARRA } from './ui';

/**
 * A gaveta de filtros avançados.
 *
 * Existe porque a barra de cima cabe três ou quatro filtros, e a consulta de
 * pedidos tem dez. Empilhá-los todos na tela empurraria a lista — que é o que
 * a pessoa veio ver — para baixo da dobra.
 *
 * **O filtro é editado em RASCUNHO e só vale no "Aplicar".** É a decisão que
 * mais importa aqui: com aplicação a cada tecla, montar um filtro de quatro
 * campos dispararia quatro consultas e a lista piscaria três vezes antes de
 * chegar onde a pessoa queria. Fechar a gaveta sem aplicar descarta o
 * rascunho, e é isso que torna seguro sair mexendo para ver o que existe.
 *
 * O contador no botão diz quantos filtros estão VALENDO — sem ele, uma gaveta
 * fechada esconde o motivo de a lista estar curta, e o suporte recebe "sumiram
 * meus pedidos".
 */
export function GavetaFiltros<T extends object>({
  valor,
  aoAplicar,
  vazio,
  children,
  contar,
}: {
  /** Os filtros que estão valendo agora. */
  valor: T;
  aoAplicar: (novo: T) => void;
  /** O estado "sem filtro nenhum", usado pelo Limpar. */
  vazio: T;
  /** Os campos, recebendo o rascunho e o modificador. */
  children: (rascunho: T, mexer: (parcial: Partial<T>) => void) => ReactNode;
  /** Quantos filtros contam como ativos. Por padrão, os campos preenchidos. */
  contar?: (valor: T) => number;
}) {
  const [aberta, setAberta] = useState(false);
  const [rascunho, setRascunho] = useState<T>(valor);

  // Ao abrir, o rascunho parte do que está valendo — e não do que ficou da
  // última vez que alguém desistiu no meio.
  useEffect(() => {
    if (aberta) setRascunho(valor);
  }, [aberta, valor]);

  useEffect(() => {
    if (!aberta) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberta(false);
    };
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberta]);

  const ativos = contar
    ? contar(valor)
    : Object.values(valor).filter((v) => v !== undefined && v !== '' && v !== null)
        .length;

  return (
    <>
      <button
        type="button"
        onClick={() => setAberta(true)}
        aria-label="Filtros avançados"
        title="Filtros avançados"
        // Com filtro ativo, a cor da marca CARREGA informação (há recorte
        // valendo) e por isso escapa do padrão; sem filtro, é um controle como
        // os outros da barra.
        className={
          ativos > 0
            ? 'border-marca-400 bg-marca-50 text-marca-600 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm transition-colors'
            : `${CONTROLE_BARRA} px-3`
        }
      >
        <SlidersHorizontal className="size-4" aria-hidden />
        Filtros
        {ativos > 0 && (
          <span className="bg-marca-600 text-marca-contraste inline-flex size-5 items-center justify-center rounded-full text-xs font-medium tabular-nums">
            {ativos}
          </span>
        )}
      </button>

      {/* Portal para o `body` — mesmo motivo do Modal: com o `<main>`
          como scroller, um overlay renderizado dentro dele deixa o fundo
          rolar por trás quando a lista de filtros termina. */}
      {aberta &&
        createPortal(
          <div className="fixed inset-0 z-50 flex justify-end">
            <div
              className="bg-palco/40 fixed inset-0"
              onClick={() => setAberta(false)}
              aria-hidden
            />

            <aside
              role="dialog"
              aria-modal="true"
              aria-label="Filtros"
              className="bg-superficie border-tinta-100 relative z-10 flex h-full w-full max-w-md flex-col border-l shadow-2xl"
            >
              <header className="border-tinta-100 flex items-center gap-2 border-b px-5 py-4">
                <SlidersHorizontal className="text-tinta-500 size-5" aria-hidden />
                <h2 className="text-tinta-900 flex-1 text-lg font-semibold">Filtros</h2>
                <button
                  type="button"
                  onClick={() => setAberta(false)}
                  aria-label="Fechar"
                  className="text-tinta-500 hover:bg-marca-600/10 hover:text-tinta-900 inline-flex size-8 items-center justify-center rounded-lg transition-colors"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </header>

              {/*
              UMA coluna: cada campo ocupa a largura inteira da gaveta.

              Eram duas, pela contagem de linhas — e a contagem estava certa
              mas media a coisa errada. Em duas colunas cada campo fica com
              ~190px, e é onde CNPJ, faixa de capital e nome de sócio param de
              caber: o valor digitado rola dentro do próprio campo e não dá
              para conferir o que se filtrou sem clicar de volta. A gaveta
              existe para PRECISAR o filtro; campo que esconde o valor
              digitado trabalha contra isso. Rolar a gaveta custa um gesto;
              não conseguir ler o que se digitou custa a conferência.

              `auto-rows-min` trava a altura da linha no conteúdo, para que o
              respiro entre campos seja só o gap medido: a gaveta é `flex-1`,
              então sobra altura, e linha que absorve sobra desmente a medida.
              Reforça o `content-start` acima em vez de repeti-lo.

              A parede `px-5 py-4` fica: é a do Drawer genérico do isotec e a
              mesma do nosso `modal.tsx`.
            */}
              <div className="rolagem-fina grid min-h-0 flex-1 auto-rows-min grid-cols-1 content-start gap-4 overflow-y-auto overscroll-contain px-5 py-4">
                {children(rascunho, (parcial) =>
                  setRascunho((atual) => ({ ...atual, ...parcial })),
                )}
              </div>

              <footer className="border-tinta-100 flex items-center gap-2 border-t px-5 py-4">
                <Botao
                  className="flex-1"
                  onClick={() => {
                    aoAplicar(rascunho);
                    setAberta(false);
                  }}
                >
                  <Search className="size-4" aria-hidden />
                  Aplicar
                </Botao>
                <Botao
                  variante="secundaria"
                  onClick={() => {
                    setRascunho(vazio);
                    aoAplicar(vazio);
                    setAberta(false);
                  }}
                >
                  <X className="size-4" aria-hidden />
                  Limpar
                </Botao>
              </footer>
            </aside>
          </div>,
          document.body,
        )}
    </>
  );
}
